import { useMemo } from 'react';
import { View, type ViewProps } from 'react-native';
import { Text } from '@/components/ui/text';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { useQuery } from '@apollo/client/react';
import { WorkoutSetDimension } from '@fytness/shared';
import { Icon } from '~/global/components/icon/Icon';
import { CurrentUserDocument } from '~/graphql/generated/graphql';
import { useLivePowerSyncQuery } from '~/powersync/hooks/useLivePowerSyncQuery';
import { useLocalizedValue } from '@/hooks/useLocalizedValue';
import { db } from '@/lib/local-api';
import type {
  WorkoutRecord,
  WorkoutSetRecord,
  WorkoutSetValueRecord,
  WorkoutExerciseRecord,
  WorkoutExerciseDimensionRecord,
  SportActivityRecord,
} from '~/powersync/generated/schema';

interface PersonalRecord {
  id: string;
  sportActivity: SportActivityRecord | undefined;
  date: string;
  value: string;
  label: string;
  variant: 'gold' | 'silver' | 'bronze';
}

const VARIANT_STYLES = {
  gold: { bg: 'bg-medal-gold-soft', icon: 'text-medal-gold' },
  silver: { bg: 'bg-medal-silver-soft', icon: 'text-medal-silver' },
  bronze: { bg: 'bg-medal-bronze-soft', icon: 'text-medal-bronze' },
} as const;

const VARIANTS = ['gold', 'silver', 'bronze'] as const;

interface PersonalRecordItemProps {
  record: PersonalRecord;
  isLast: boolean;
}

const PersonalRecordItem = ({ record, isLast }: PersonalRecordItemProps) => {
  const styles = VARIANT_STYLES[record.variant];
  const exerciseName = useLocalizedValue(record.sportActivity?.name);

  return (
    <View
      className={`flex flex-row items-center gap-3.5 p-4 ${!isLast ? 'border-b border-border' : ''}`}
    >
      <View
        className={`h-11 w-11 rounded-lg items-center justify-center ${styles.bg}`}
      >
        <Icon name="Trophy" size={20} className={styles.icon} />
      </View>
      <View className="flex-1 gap-0.5">
        <Text className="font-inter-medium text-sm text-text-primary">
          {exerciseName}
        </Text>
        <Text className="font-inter-regular text-xs text-text-muted">
          {record.date}
        </Text>
      </View>
      <View className="items-end gap-0.5">
        <Text className="font-sora-bold text-base text-accent">
          {record.value}
        </Text>
        <Text className="font-inter-medium text-[10px] text-text-muted">
          {record.label}
        </Text>
      </View>
    </View>
  );
};

export interface PersonalRecordsSectionProps extends ViewProps {}

export const PersonalRecordsSection = ({
  className,
  ...props
}: PersonalRecordsSectionProps) => {
  const { t } = useTranslation('workout');

  const { data: userData } = useQuery(CurrentUserDocument);
  const currentUserId = userData?.currentUser?.id;

  const { data: workouts } = useLivePowerSyncQuery<WorkoutRecord>(
    db.Workout.queries.findByUserId(currentUserId ?? ''),
    [currentUserId],
    { enabled: !!currentUserId },
  );

  const workoutIds = useMemo(() => workouts.map((w) => w.id), [workouts]);
  const workoutIdsKey = workoutIds.join(',');

  const { data: setValues } = useLivePowerSyncQuery<WorkoutSetValueRecord>(
    db.Workout.queries.findSetValuesByWorkoutIds(workoutIds),
    [workoutIdsKey],
  );
  const { data: dimensions } =
    useLivePowerSyncQuery<WorkoutExerciseDimensionRecord>(
      db.Workout.queries.findDimensionsByWorkoutIds(workoutIds),
      [workoutIdsKey],
    );
  const { data: sets } = useLivePowerSyncQuery<WorkoutSetRecord>(
    db.Workout.queries.findSetsByWorkoutIds(workoutIds),
    [workoutIdsKey],
  );
  const { data: exercises } = useLivePowerSyncQuery<WorkoutExerciseRecord>(
    db.Workout.queries.findExercisesByWorkoutIds(workoutIds),
    [workoutIdsKey],
  );

  const sportActivityIds = useMemo(
    () =>
      Array.from(
        new Set(
          exercises
            .map((e) => e.sportActivityId)
            .filter((id): id is string => !!id),
        ),
      ),
    [exercises],
  );
  const sportActivityIdsKey = sportActivityIds.join(',');

  const { data: sportActivities } = useLivePowerSyncQuery<SportActivityRecord>(
    db.SportActivity.queries.findByIds(sportActivityIds),
    [sportActivityIdsKey],
  );

  const records = useMemo<PersonalRecord[]>(() => {
    const dimensionMap = new Map(dimensions.map((d) => [d.id, d]));
    const exerciseMap = new Map(exercises.map((e) => [e.id, e]));
    const setById = new Map(sets.map((s) => [s.id, s]));
    const sportActivityMap = new Map(sportActivities.map((s) => [s.id, s]));

    // Group completed set values by their set so weight and reps can be paired.
    const valuesBySet = new Map<string, WorkoutSetValueRecord[]>();
    for (const value of setValues) {
      const setId = value.setId;
      if (!setId) continue;
      if (!setById.get(setId)?.completedAt) continue;
      const list = valuesBySet.get(setId) ?? [];
      list.push(value);
      valuesBySet.set(setId, list);
    }

    // Best single set (by weight × reps) per exercise definition.
    const bestBySportActivity = new Map<
      string,
      {
        volume: number;
        weight: number;
        reps: number;
        unit: string;
        date: string;
      }
    >();

    for (const [setId, values] of valuesBySet) {
      let weight = 0;
      let reps = 0;
      let unit = '';
      for (const value of values) {
        const dimension = dimensionMap.get(value.exerciseDimensionId ?? '');
        if (dimension?.dimension === WorkoutSetDimension.WEIGHT) {
          weight += Number(value.value) || 0;
          unit = dimension.unit ?? '';
        } else if (dimension?.dimension === WorkoutSetDimension.REPS) {
          reps += Number(value.value) || 0;
        }
      }
      if (weight <= 0 || reps <= 0) continue;

      const set = setById.get(setId);
      const exercise = set?.exerciseId
        ? exerciseMap.get(set.exerciseId)
        : undefined;
      const sportActivityId = exercise?.sportActivityId;
      if (!sportActivityId) continue;

      const volume = weight * reps;
      const current = bestBySportActivity.get(sportActivityId);
      if (!current || volume > current.volume) {
        bestBySportActivity.set(sportActivityId, {
          volume,
          weight,
          reps,
          unit,
          date: set?.completedAt ?? '',
        });
      }
    }

    return Array.from(bestBySportActivity.entries())
      .sort(([, a], [, b]) => b.volume - a.volume)
      .slice(0, 3)
      .map(([sportActivityId, best], index) => {
        // The weight dimension may not carry a unit; the app treats weights as
        // kg by default, so fall back to that rather than showing a bare number.
        const unitKey = best.unit.toUpperCase();
        const unitShort =
          unitKey && unitKey !== 'NONE'
            ? t(`unit.${unitKey}.shortName`)
            : t('unit.KG.shortName');
        const reps = Math.round(best.reps);
        return {
          id: sportActivityId,
          sportActivity: sportActivityMap.get(sportActivityId),
          date: best.date ? format(new Date(best.date), 'MMM d, yyyy') : '',
          value: `${Math.round(best.weight)}${unitShort ? ` ${unitShort}` : ''}`,
          label: t('personalRecords.reps', { count: reps }),
          variant: VARIANTS[index],
        };
      });
  }, [setValues, dimensions, sets, exercises, sportActivities, t]);

  return (
    <View className={className} {...props}>
      <View className="flex flex-row items-center mb-3 gap-3">
        <View className="h-0.5 w-6 rounded-sm bg-accent" />
        <Text className="font-inter-bold text-xs tracking-widest text-text-secondary uppercase">
          {t('personalRecords.title')}
        </Text>
      </View>

      <View className="rounded-xl bg-surface overflow-hidden">
        {records.length === 0 ? (
          <View className="p-4">
            <Text className="font-inter-regular text-sm text-text-muted">
              {t('personalRecords.empty')}
            </Text>
          </View>
        ) : (
          records.map((record, index) => (
            <PersonalRecordItem
              key={record.id}
              record={record}
              isLast={index === records.length - 1}
            />
          ))
        )}
      </View>
    </View>
  );
};
